import { useRef, useState, useEffect } from 'react';
import { Flexbox, Text } from '@morressier/ts';
import { faSquarePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmailAffiliation from './EmailAffiliation';
import MissingProfileIndicator from '../MissingProfileIndicator';
import AffiliationItem from './AffiliationItem';
import { countriesByAlpha2 } from '../countriesList';
import { LinkButton } from 'components/ProfileUIComponents';
import { IProfile } from 'typings/profile';
import { KeyedMutator } from 'swr';
import { IAffiliation, IAffiliationResponse } from 'typings/organization';
import { typedBoolean } from 'utils/utils';
import { getQuery } from 'utils/query';
import { useRouter } from 'next/router';
import { faBriefcase } from '@fortawesome/pro-light-svg-icons';
import { logEvent } from 'utils/eventTracking';

const addAffiliationText =
  'Showcase your proven experience to potential collaborators, conference organisers, and publishers with verified professional affiliations.';

interface IAffiliationsProps {
  isSameUser?: boolean;
  userId: string;
  affiliationId?: string;
  mutateProfile: KeyedMutator<IProfile>;
  data: IAffiliation[];
  mutate: KeyedMutator<IAffiliationResponse>;
  isLoading: boolean;
}

const Affiliations = (props: IAffiliationsProps) => {
  const router = useRouter();
  const {
    isSameUser,
    userId,
    affiliationId,
    mutateProfile,
    isLoading,
    mutate,
    data
  } = props;
  const [showAffiliationForm, setShowAffiliationForm] = useState(false);
  const isEditView = useRef<boolean>(false);
  const selectedItemKey = useRef<string>('');

  useEffect(() => {
    const queryParams = getQuery(router);
    if (queryParams?.modal === 'add-affiliation') {
      openAffiliationForm();
    }
  }, [router]);

  const openAffiliationForm = () => {
    setShowAffiliationForm(true);
  };
  const closeAffiliationForm = (updateData?: IAffiliation) => {
    if (updateData) {
      updateItemData(updateData);
    }
    isEditView.current = false;
    selectedItemKey.current = '';
    setShowAffiliationForm(false);
  };

  const onEditClick = (id: string) => {
    isEditView.current = true;
    selectedItemKey.current = id;
    logEvent('PROFILE_EDIT_AFFILIATION_CLICKED', { affiliation_id: id });
    openAffiliationForm();
  };

  const selectedItemData = () => {
    const filtered = data.filter((affiliation: IAffiliation) => {
      if (affiliation.id == selectedItemKey.current) {
        return affiliation;
      }
    });
    if (filtered && filtered.length > 0) {
      return filtered[0];
    }
  };

  const updateItemData = (updatedData?: IAffiliation) => {
    if (updatedData?.role) {
      let index = -1;
      for (let i = 0; i < data.length; i++) {
        if (data[i].id == selectedItemKey.current) {
          index = i;
          break;
        }
      }
      if (index != -1) {
        data[index].role = updatedData?.role ?? '';
        data[index].startDate = updatedData?.startDate ?? '';
        data[index].endDate = updatedData?.endDate ?? '';
      }
    }
  };

  const affiliationItem = selectedItemData();

  return (
    <div id="affiliations">
      <Flexbox
        gap="8px"
        flexDirection="row"
        alignItems="center"
        style={{ marginBottom: '1rem' }}
      >
        <Text size="h4_new" fontWeight="semiBold">
          Affiliations
        </Text>
        {data.length > 0 && isSameUser && (
          <LinkButton
            onClick={() => {
              logEvent('PROFILE_ADD_AFFILIATION_CLICKED');
              openAffiliationForm();
            }}
          >
            <FontAwesomeIcon icon={faSquarePlus} />
            Add Affiliation
          </LinkButton>
        )}
      </Flexbox>
      {data.length === 0 && isSameUser && !isLoading && (
        <MissingProfileIndicator
          handleAction={() => {
            logEvent('PROFILE_ADD_AFFILIATION_CLICKED');
            openAffiliationForm();
          }}
          title="Add your affiliations"
          actiontitle="Add affiliation"
          description={addAffiliationText}
          icon={faBriefcase}
          actionIcon={faSquarePlus}
        />
      )}
      <Flexbox gap="1rem" flexDirection="column">
        {data.map(
          ({
            id,
            organizationName,
            organizationType,
            department,
            location,
            startDate,
            endDate,
            role,
            validatedAt,
            organizationId,
            createdAt,
            email,
            type,
            isEmailDomainMatchesOrgDomain
          }) => (
            <AffiliationItem
              key={id}
              refetchAffiliations={mutate}
              id={id}
              title={role}
              organization={organizationName}
              department={department}
              location={
                location?.length === 2
                  ? countriesByAlpha2[location]?.name
                  : location
              }
              startDate={startDate}
              endDate={endDate}
              validatedAt={validatedAt}
              organizationId={organizationId}
              organizationType={organizationType}
              isSameUser={isSameUser}
              onEditClick={onEditClick}
              createdAt={createdAt}
              email={email}
              userId={userId}
              type={type}
              affiliationType={type}
              isEmailDomainMatchesOrgDomain={isEmailDomainMatchesOrgDomain}
              isPrimaryAffiliation={affiliationId === id}
              mutateProfile={mutateProfile}
            />
          )
        )}
      </Flexbox>
      <EmailAffiliation
        isPrimaryAffiliation={
          typedBoolean(affiliationId)
            ? affiliationId === affiliationItem?.id
            : !isEditView.current
        }
        isOpen={showAffiliationForm}
        onClose={closeAffiliationForm}
        showEditView={isEditView.current}
        affiliationItem={affiliationItem}
        userId={userId}
        mutateProfile={mutateProfile}
        refetchAffiliations={mutate}
      />
    </div>
  );
};

export default Affiliations;
